// Section visibility configuration for SpreadsheetGrowthComparison
// Single source of truth for screen rendering AND PDF export

export const SECTION_GROUPS = [
  {
    id: 'growth',
    label: 'Growth Analysis',
    color: 'text-teal-700',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    sections: [
      { id: 'growthChart', label: 'Growth vs Model Chart' },
      { id: 'sampleMetrics', label: 'Summary Metrics' },
      { id: 'feedingMetrics', label: 'Feeding & Mortality Metrics' },
      { id: 'dataTable', label: 'Daily Data Table' },
    ],
  },
  {
    id: 'feed',
    label: 'Feed & Efficiency',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    sections: [
      { id: 'biologicalFCR', label: 'Biological FCR' },
      { id: 'mortalityChart', label: 'Mortality Chart' },
      { id: 'feedChart', label: 'Feed Analysis Chart' },
      { id: 'feedVariance', label: 'Feed Variance Comparison' },
      { id: 'feedSummary', label: 'Feeding Recommendation' },
      { id: 'economicParams', label: 'Economic Impact' },
    ],
  },
  {
    id: 'stock',
    label: 'Stock Intelligence',
    color: 'text-purple-700',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    sections: [
      { id: 'stockIntegrity', label: 'Stock Integrity Score' },
      { id: 'fcrAnalysis', label: 'FCR Detection Analysis' },
      { id: 'sampleJump', label: 'Sample Jump Analysis' },
      { id: 'intelligenceReport', label: 'Missing Fish Intelligence' },
    ],
  },
  {
    id: 'diagnostics',
    label: 'Advanced Diagnostics',
    color: 'text-indigo-700',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    sections: [
      { id: 'cycleComparison', label: 'Cycle Comparison' },
    ],
  },
];

// Flat list of all section IDs
export const ALL_SECTION_IDS = SECTION_GROUPS.flatMap(g => g.sections.map(s => s.id));

// Build a visibility state object where every section has the given value
const makeState = (value) =>
  Object.fromEntries(ALL_SECTION_IDS.map(id => [id, value]));

export const ALL_VISIBLE = makeState(true);
export const NONE_VISIBLE = makeState(false);

// Presets
export const PRESETS = [
  {
    id: 'growth',
    label: 'Growth View',
    state: {
      ...NONE_VISIBLE,
      growthChart: true,
      sampleMetrics: true,
      feedingMetrics: true,
      dataTable: true,
    },
  },
  {
    id: 'feed',
    label: 'Feed View',
    state: {
      ...NONE_VISIBLE,
      biologicalFCR: true,
      mortalityChart: true,
      feedChart: true,
      feedVariance: true,
      feedSummary: true,
      economicParams: true,
    },
  },
  {
    id: 'diagnostics',
    label: 'Diagnostics View',
    state: {
      ...NONE_VISIBLE,
      stockIntegrity: true,
      fcrAnalysis: true,
      sampleJump: true,
      intelligenceReport: true,
      cycleComparison: true,
    },
  },
  {
    id: 'custom',
    label: 'Custom',
    state: null, // keeps current state
  },
];

// Which preset matches a given state (returns 'custom' if none match)
export const detectPreset = (state) => {
  for (const preset of PRESETS) {
    if (!preset.state) continue;
    const match = ALL_SECTION_IDS.every(id => !!state[id] === !!preset.state[id]);
    if (match) return preset.id;
  }
  return 'custom';
};

// At least one section visible?
export const hasAnyVisible = (state) => ALL_SECTION_IDS.some(id => state[id]);

// Admin visibility key → section ID mapping
// When admin hides a page.* key, the corresponding section is removed from controls
export const ADMIN_PAGE_KEY_TO_SECTION = {
  'page.feed_variance_comparison': 'feedVariance',
  'page.stock_integrity_score': 'stockIntegrity',
};

// Given admin visibility object, return a Set of section IDs that admin has hidden
export const getAdminHiddenSections = (adminVisibility) => {
  const hidden = new Set();
  if (!adminVisibility) return hidden;
  for (const [adminKey, sectionId] of Object.entries(ADMIN_PAGE_KEY_TO_SECTION)) {
    if (adminVisibility[adminKey] === false) {
      hidden.add(sectionId);
    }
  }
  return hidden;
};
